// organizationsApi.ts
import { baseApi } from './base-api';
import { getIdentityGeneration } from '../utils/identity-scope';
import type { User } from './users-api';
import { API_ENDPOINTS } from '../utils/constants';
import { withoutSipCredentialMetadata } from '../utils/sip-settings';
import type { Integrations } from '~/redux/reducers/features/integrationsSlice';
import type { Subscription } from '~/redux/reducers/features/subscriptionSlice';

interface SubscriptionResponse {
  type: string;
  response: Subscription;
}

interface SubscriptionStatusResponse {
  type: string;
  response: {
    status: string | null;
  };
}
interface Organization {
  id: string;
  metadata: Record<string, any>;
  partner_access?: boolean;
  organizations?: [];
  partner?: string;
}

interface Metadata {
  company_name: string;
}

interface OrganizationUsersQueryArgs {
  org_id: string;
  uid?: string;
  filters?: { page?: number; rows_per_page?: number };
}

interface OrganizationUsersResponse {
  type: string;
  response: User[];
}

interface PendingInvitesResponse {
  type: string;
  response: { count: number };
}

export interface CreateOrganizationArgs {
  partner: string; // partner_id
  user: object; //user id
  metadata: {
    company_name: string;
    country: string;
    website_url: string;
    consent: boolean;
  };
}
export interface CreatePartnerArgs {
  metadata: {
    company_name: string;
    country: string;
    website_url: string;
    consent: boolean;
  };
}

export interface OrganizationResponse {
  id: string;
  metadata: Record<string, any>;
}

export type SipTrunkSyncStatus = 'synchronized' | 'pending' | 'failed';

export type UpdateIntegrationsResult = Integrations & {
  id: string;
  sip_trunk_sync?: SipTrunkSyncStatus;
};

interface UpdateIntegrationsArgs {
  id: string;
  updatedData: Partial<Integrations>;
  // SIP triggers routing synchronization; number and blacklist saves must opt in.
  includeSip?: boolean;
}

interface UpdateIntegrationsApiResponse {
  type: string;
  response: string;
  sip_trunk_sync?: SipTrunkSyncStatus;
}

export interface SipTokenResponse {
  token: string;
  sip_uri: string | null;
  headers: {
    'X-Karla-Organization-ID': string;
    'X-Karla-SIP-Token': string;
  };
}

export interface SipTokenIssueResponse extends SipTokenResponse {
  sip_trunk_sync: SipTrunkSyncStatus;
}

export interface SipTokenRevokeResponse {
  token_configured: false;
  sip_trunk_sync: SipTrunkSyncStatus;
}

const toEditableIntegrationPayload = (updatedData: Partial<Integrations>, includeSip = false): Partial<Integrations> => {
  const payload = { ...updatedData };
  if (!includeSip) delete payload.sip;
  return withoutSipCredentialMetadata(payload);
};

export interface PartnerBilling {
  plan: string;
  status: string;
  price: number;
  currency: string;
  interval_unit: string;
  interval_value: number;
  invoice_issuer: string;
  start_date: string | null;
  end_date: string | null;
  custom_commission: number | null;
}

export interface PartnerDetailResponse {
  id: string;
  metadata: {
    company_name: string;
    country: string;
    website_url: string;
    note?: string | null;
  };
  created: string;
  commission: { global_commission: number } | null;
  billing: PartnerBilling | null;
  organizations: { id: string; metadata: { company_name: string }; created: string; partner_access?: boolean; invoice_issuer?: string | null; billing_status?: string | null; custom_commission?: number | null }[];
}

export interface OrganizationUsageMetric {
  usage: number;
  included: number | null;
  remaining: number | null;
  ratio: number;
  percentage: number;
  status: 'normal' | 'warning' | 'exceeded';
  overage_units: number;
  estimated_overage: number;
  unit?: string;
}

export interface OrganizationUsageResponse {
  status: 'active' | 'inactive' | 'no_billing';
  organization_id: string;
  organization: { id: string; name: string };
  period: { start: string; end: string; end_is_exclusive: boolean } | null;
  billing?: {
    plan: string | number;
    status: string;
    price: number;
    currency: string;
    usage_interval_unit: string;
    usage_interval_value: number;
  };
  metrics: {
    chat: OrganizationUsageMetric;
    search: OrganizationUsageMetric;
    draft: OrganizationUsageMetric;
    voice: OrganizationUsageMetric;
  };
  has_warning: boolean;
  has_overage: boolean;
  no_traffic_last_7_days: boolean;
  last_updated?: string | null;
  estimated_overage: number;
}

export interface OrganizationUsageSummaryResponse {
  organization: { id: string; name: string };
  usage: Omit<OrganizationUsageResponse, 'organization'> & {
    history?: { period: { start: string; end: string }; has_overage: boolean; usage: Record<string, number> }[];
    overage_streak?: { periods: number; since: string | null };
    overage_streaks?: Record<string, { periods: number; since: string | null }>;
  };
  summary: {
    headline: string;
    severity: 'low' | 'medium' | 'high';
    summary: string;
    recommendation: string;
  };
}

const organizationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<Organization[], void>({
      query: () => {
        return {
          url: '/organizations',
          method: 'GET',
        };
      },
      transformResponse: (response: { type: string; response: Organization[] }) => {
        return response.response;
      },
      providesTags: ['Organization'],
    }),

    getOrganizationsBilling: builder.query<any, void>({
      query: () => {
        return {
          url: '/organizations/billing',
          method: 'GET',
        };
      },
    }),

    getOrganizationById: builder.query<Organization, string>({
      query: (id) => {
        const url = new URL(`/v1/organizations/${id}`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),

    getOrganizationIntegrations: builder.query<Integrations, string>({
      query: (id) => {
        const url = new URL(`/v1/organizations/${id}/integrations`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      transformResponse: (response: { type: string; response: Integrations }) => {
        return response.response;
      },
      providesTags: (result, error, id) => [{ type: 'Integrations', id }],
    }),

    // The panel resets this mutation after each request to keep the token out of a persistent query cache.
    getOrganizationSipToken: builder.mutation<SipTokenResponse, string>({
      query: (orgId) => ({
        url: new URL(`/v1/organizations/${orgId}/integrations/sip/token`, API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'GET',
      }),
      transformResponse: (response: { type: string; response: SipTokenResponse }) => response.response,
    }),

    // The SIP dialog refreshes integration status after credential changes.
    createOrganizationSipToken: builder.mutation<SipTokenIssueResponse, string>({
      query: (orgId) => ({
        url: new URL(`/v1/organizations/${orgId}/integrations/sip/token`, API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'POST',
      }),
      transformResponse: (response: { type: string; response: SipTokenIssueResponse }) => response.response,
    }),

    revokeOrganizationSipToken: builder.mutation<SipTokenRevokeResponse, string>({
      query: (orgId) => ({
        url: new URL(`/v1/organizations/${orgId}/integrations/sip/token`, API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'DELETE',
      }),
      transformResponse: (response: { type: string; response: SipTokenRevokeResponse }) => response.response,
    }),

    createOrganization: builder.mutation<void, CreateOrganizationArgs>({
      query: (payload) => ({
        url: '/organizations',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Organization'],
    }),

    createPartnerOrganization: builder.mutation<void, CreatePartnerArgs>({
      query: (payload) => ({
        url: '/partners',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Organization'],
    }),

    getPartners: builder.query<Organization[], void>({
      query: () => ({
        url: '/partners',
        method: 'GET',
      }),
      transformResponse: (response: { type: string; response: Organization[] }) => {
        return response.response;
      },
      providesTags: ['Organization'],
    }),

    getPartnerById: builder.query<PartnerDetailResponse, string>({
      query: (id) => ({
        url: `/partners/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { type: string; response: PartnerDetailResponse }) => {
        return response.response;
      },
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),

    updatePartner: builder.mutation<void, { id: string; note?: string; commission?: number; invoice_issuer?: string }>({
      query: ({ id, ...body }) => ({
        url: `/partners/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Organization', id }],
    }),

    deletePartner: builder.mutation<void, string>({
      query: (id) => ({
        url: `/partners/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization'],
    }),

    addOrgToPartner: builder.mutation<void, { partnerId: string; orgId: string }>({
      query: ({ partnerId, orgId }) => ({
        url: `/partners/${partnerId}/organizations/${orgId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { partnerId }) => [{ type: 'Organization', id: partnerId }, 'Organization'],
    }),

    removeOrgFromPartner: builder.mutation<void, { partnerId: string; orgId: string }>({
      query: ({ partnerId, orgId }) => ({
        url: `/partners/${partnerId}/organizations/${orgId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { partnerId }) => [{ type: 'Organization', id: partnerId }, 'Organization'],
    }),

    updateOrganization: builder.mutation<Organization, { id: string; updatedData: Partial<Organization> }>({
      query: ({ id, updatedData }) => ({
        url: `organizations/${id}`,
        method: 'PATCH',
        body: updatedData,
      }),
      transformResponse: (response, meta, arg) =>
        ({
          id: arg.id,
          ...arg.updatedData,
        }) as Organization,
      onQueryStarted: async ({ id, updatedData }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getOrganizationById', id, (draft) => {
            Object.assign(draft, updatedData);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (generation === getIdentityGeneration()) patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Organization', id }],
    }),
    updateIntegrations: builder.mutation<UpdateIntegrationsResult, UpdateIntegrationsArgs>({
      query: ({ id, updatedData, includeSip }) => ({
        url: `organizations/${id}/integrations`,
        method: 'PATCH',
        body: toEditableIntegrationPayload(updatedData, includeSip),
      }),
      transformResponse: (response: UpdateIntegrationsApiResponse, meta, arg) =>
        ({
          id: arg.id,
          ...toEditableIntegrationPayload(arg.updatedData, arg.includeSip),
          sip_trunk_sync: response.sip_trunk_sync,
        }) as UpdateIntegrationsResult,
      onQueryStarted: async ({ id, updatedData, includeSip }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        const { sip, ...integrations } = toEditableIntegrationPayload(updatedData, includeSip);
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getOrganizationIntegrations', id, (draft: Integrations) => {
            Object.assign(draft, integrations);
            if (sip) draft.sip = { ...draft.sip, ...sip };
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (generation === getIdentityGeneration()) patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Integrations', id },
        { type: 'Integrations', id: 'INBOX_SIMULATION_CONTEXTS' },
      ],
    }),
    // Query to fetch all users of an organization
    getOrganizationUsers: builder.query<OrganizationUsersResponse, OrganizationUsersQueryArgs>({
      query: ({ org_id }) => ({
        url: `organizations/${org_id}/users`,
        method: 'GET',
      }),
      providesTags: (result, error, { org_id }) => [
        { type: 'Users', id: org_id },
        { type: 'Organization', id: org_id },
      ],
    }),
    getOrganizationPendingInvites: builder.query<number, string>({
      query: (org_id) => ({
        url: `organizations/${org_id}/invites/pending`,
        method: 'GET',
      }),
      transformResponse: (response: PendingInvitesResponse) => response.response.count,
      providesTags: (result, error, org_id) => [{ type: 'PendingInvitations', id: org_id }],
    }),
    deleteOrganizationUsers: builder.mutation<void, { org_id: string; ids: string[] }>({
      query: ({ org_id, ids }) => ({
        url: `organizations/${org_id}/users`,
        method: 'DELETE',
        body: { ids },
      }),
      onQueryStarted: async ({ org_id, ids }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        // Optimistically remove users from the list cache
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getOrganizationUsers', { org_id }, (draft) => {
            ids.forEach((id) => {
              const idx = draft.findIndex((u) => u.id === id);
              if (idx !== -1) draft.splice(idx, 1);
            });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (generation === getIdentityGeneration()) patchResult.undo(); // rollback on failure
        }
      },
      invalidatesTags: (result, error, { org_id, ids }) => [{ type: 'Users', id: org_id }, ...ids.map((id) => ({ type: 'User', id }))],
    }),
    deleteOrganizations: builder.mutation<void, { ids: string[] }>({
      query: ({ ids }) => ({
        url: 'organizations',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['Organization'],
    }),
    getSubscription: builder.query<SubscriptionResponse, string>({
      query: (id) => {
        const url = new URL(`/v1/organization/${id}/subscription`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          method: 'GET',
          url: url.toString(),
        };
      },
      providesTags: (result, error, id) => [{ type: 'Subscription', id }],
    }),

    getSubscriptionStatus: builder.query<SubscriptionStatusResponse, string>({
      query: (id) => {
        const url = new URL(`/v1/organization/${id}/subscription/status`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          method: 'GET',
          url: url.toString(),
        };
      },
      providesTags: (result, error, id) => [{ type: 'Subscription', id }],
    }),

    getOrganizationUsage: builder.query<OrganizationUsageResponse, string>({
      query: (id) => {
        const url = new URL(`/v1/organization/${id}/usage`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          method: 'GET',
          url: url.toString(),
        };
      },
      transformResponse: (response: { type: string; response: OrganizationUsageResponse }) => response.response,
      providesTags: (result, error, id) => [{ type: 'OrganizationUsage', id }],
    }),

    getOrganizationUsageSummary: builder.query<OrganizationUsageSummaryResponse, string>({
      query: (id) => {
        const url = new URL(`/v1/organization/${id}/usage/summary`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          method: 'GET',
          url: url.toString(),
        };
      },
      transformResponse: (response: { type: string; response: OrganizationUsageSummaryResponse }) => response.response,
    }),

    updateOrgBillingFields: builder.mutation<void, { id: string; billing: { custom_commission?: number | null; invoice_issuer?: string } }>({
      query: ({ id, billing }) => {
        const url = new URL(`/v1/organization/${id}/subscription`, API_ENDPOINTS.KARLA_FLASK_API);
        return { url: url.toString(), method: 'PATCH', body: { subscription: { billing } } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Organization', id }],
    }),

    updateSubscription: builder.mutation<SubscriptionResponse, { id: string; updatedData: Partial<Subscription> }>({
      query: ({ id, updatedData }) => {
        const url = new URL(`/v1/organization/${id}/subscription`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'PATCH',
          body: updatedData,
        };
      },
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        try {
          const { data } = await queryFulfilled;
          if (generation !== getIdentityGeneration()) return;
          dispatch(
            baseApi.util.updateQueryData('getSubscription', id, (draft) => {
              draft.response = data.response;
            }),
          );
        } catch {
          // Keep the cached subscription unchanged when the server rejects the update.
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Subscription', id }],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationsBillingQuery,
  useGetOrganizationByIdQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useUpdateIntegrationsMutation,
  useGetOrganizationSipTokenMutation,
  useCreateOrganizationSipTokenMutation,
  useRevokeOrganizationSipTokenMutation,
  useGetOrganizationUsersQuery,
  useGetOrganizationPendingInvitesQuery,
  useDeleteOrganizationUsersMutation,
  useCreatePartnerOrganizationMutation,
  useGetPartnersQuery,
  useGetPartnerByIdQuery,
  useUpdatePartnerMutation,
  useDeletePartnerMutation,
  useAddOrgToPartnerMutation,
  useRemoveOrgFromPartnerMutation,
  useDeleteOrganizationsMutation,
  useGetOrganizationIntegrationsQuery,
  useGetSubscriptionQuery,
  useGetSubscriptionStatusQuery,
  useGetOrganizationUsageQuery,
  useGetOrganizationUsageSummaryQuery,
  useUpdateSubscriptionMutation,
  useUpdateOrgBillingFieldsMutation,
} = organizationsApi;

export { organizationsApi };
